import { useState } from 'react';
import { message } from 'antd';
import { apiConfig } from '../authConfig';
import { apiErrorFromResponse, userMessageForError } from '../utils/apiError';

/**
 * Shared AI-draft-review logic for the advisory-firm profile (Commandment IV: nothing from
 * POST /api/v1/me/advisory-firm/ai-profile is ever written into the antd `form` except through
 * an explicit, per-field "Apply" call this hook exposes). Used by both
 * pages/AdvisoryFirmOnboarding.jsx (wizard step 1) and pages/AdvisoryFirmSettings.jsx (edit
 * form) — extracted here so both call sites share one fetch/parse/apply implementation rather
 * than re-deriving it (2 real call sites; not a speculative abstraction).
 *
 * `paymentLinksJson` is never read from the draft — see the modal component's own note.
 *
 * BACKEND NOT YET LIVE this wave — see .claude/design/la-onboarding-razorpay.md. Failures are
 * surfaced in `aiModal.error`, never silently treated as an empty-but-successful draft.
 */
export function useAdvisoryFirmAiDraft(form, getApiToken) {
    const [aiModal, setAiModal] = useState({
        open: false,
        loading: false,
        error: null,
        draft: null,
        appliedKeys: new Set(),
    });

    const runAiEnrichment = async () => {
        const { website, linkedInUrl } = form.getFieldsValue(['website', 'linkedInUrl']);
        if (!website && !linkedInUrl) {
            message.warning('Enter a website or LinkedIn URL first.');
            return;
        }
        setAiModal({ open: true, loading: true, error: null, draft: null, appliedKeys: new Set() });
        try {
            const token = await getApiToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/me/advisory-firm/ai-profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ website, linkedInUrl }),
            });
            const json = await res.json().catch(() => null);
            if (!res.ok || !json || !json.success) {
                throw apiErrorFromResponse(res, json);
            }
            // Assumed response shape per .claude/design/la-onboarding-razorpay.md — NOT verified
            // against a live backend this wave.
            const draft = json.data?.draft || json.data || {};
            setAiModal((prev) => ({ ...prev, loading: false, draft }));
        } catch (err) {
            setAiModal((prev) => ({ ...prev, loading: false, error: userMessageForError(err, 'AI enrichment could not be completed. Please try again.') }));
        }
    };

    const applyDraftField = (key, value) => {
        form.setFieldValue(key, value);
        setAiModal((prev) => ({ ...prev, appliedKeys: new Set(prev.appliedKeys).add(key) }));
    };

    const applyAllDraftFields = () => {
        setAiModal((prev) => {
            if (!prev.draft) return prev;
            const next = new Set(prev.appliedKeys);
            Object.entries(prev.draft).forEach(([key, value]) => {
                if (key === 'confidence' || key === 'reasoningRefs') return;
                if (value == null || value === '') return;
                form.setFieldValue(key, value);
                next.add(key);
            });
            return { ...prev, appliedKeys: next };
        });
    };

    const closeAiModal = () => setAiModal((prev) => ({ ...prev, open: false }));

    return { aiModal, runAiEnrichment, applyDraftField, applyAllDraftFields, closeAiModal };
}
